import { takeLatest, put, select, call } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';

import callApi, { PATHS, METHODS } from 'api';
import { setAccessToken, setRefreshToken, setUserId, setInitialAuthDone } from 'features/Auth/slice';
import { setAuthData } from 'features/Auth/actions';
import { setId, setUsername, setEmail } from 'features/User/slice';
import { submitRegistration, registrationSuccess, registrationFailure } from './actions';
import { 
  setRegistrationLoading, 
  setRegistrationError,
  resetRegistration 
} from './slice';
import { getUserData } from './selectors';
import type { OnboardingFormData } from './types';

interface RegisterResponse {
  access_token: string;
  refresh_token: string;
  id: string;
}

interface RegisterError {
  customStatusCode?: number;
  custom_status_code?: number;
  detail?: string;
  message?: string;
}

function* handleSubmitRegistration(action: PayloadAction<OnboardingFormData>): Generator<any, void, any> {
  try {
    yield put(setRegistrationLoading(true));
    yield put(setRegistrationError(null));

    // Get the complete user data from Redux
    const userData = yield select(getUserData);
    const registrationData = {
      ...userData,
      ...action.payload,
    };

    // First, create an anonymous user
    const anonymousResponse: any = yield call(callApi, {
      method: METHODS.POST,
      mainPath: PATHS.CREATE_ANONYMOUS_USER,
      data: {},
    });


    const anonymousUserId = anonymousResponse?.data?.user_id || anonymousResponse?.data?.id;
    
    if (!anonymousUserId) {
      throw new Error('Failed to create anonymous user');
    }

    // Generate a random password for registration (following iOS pattern)
    const randomPassword = Math.random().toString(36).slice(-8) + 
                          Math.random().toString(36).slice(-8).toUpperCase();

    // Call registration API with anonymous user ID
    const registerResponse: any = yield call(callApi, {
      method: METHODS.POST,
      mainPath: PATHS.REGISTER,
      data: {
        user_id: anonymousUserId,
        email: registrationData.email,
        password: randomPassword,
        name: registrationData.name,
        industry: registrationData.industry,
        role: registrationData.role,
      },
    });


    // Registration should return tokens directly
    if (registerResponse?.data?.access_token && registerResponse?.data?.refresh_token) {
      // Save auth tokens from registration using setAuthData to persist to storage
      yield put(setAuthData({
        accessToken: registerResponse.data.access_token,
        refreshToken: registerResponse.data.refresh_token,
        flow: 'registration',
      }));
      
      // Save user ID from registration
      if (registerResponse.data.id) {
        yield put(setUserId(registerResponse.data.id));
        yield put(setId(registerResponse.data.id));
      }
    } else {
      // Fallback: If no tokens in registration, we need to login
      
      const loginResponse: any = yield call(callApi, {
        method: METHODS.POST,
        mainPath: PATHS.LOGIN,
        data: {
          email: registrationData.email,
          password: randomPassword,
        },
      });


      // Save auth tokens from login using setAuthData to persist to storage
      yield put(setAuthData({
        accessToken: loginResponse.data.access,
        refreshToken: loginResponse.data.refresh,
        flow: 'registration',
      }));
    }

    // Save user data we already have
    yield put(setUsername(registrationData.name));
    yield put(setEmail(registrationData.email));

    // Fetch user info to get complete user details
    try {
      const userInfoResponse: any = yield call(callApi, {
        method: METHODS.GET,
        mainPath: PATHS.USER_INFO,
        authorized: true,
      });


      // Update with any additional user data
      const userInfo = userInfoResponse?.data;
      if (userInfo) {
        yield put(setUserId(userInfo.id));
        yield put(setId(userInfo.id));
        // Update email if different
        if (userInfo.email) {
          yield put(setEmail(userInfo.email));
        }
      }
    } catch (error) {
      // Failed to fetch user info, but registration was successful
    }

    // Mark auth as initialized
    yield put(setInitialAuthDone(true));

    // Industry and role are already saved during registration

    // Mark registration as successful
    yield put(registrationSuccess());

    // Clear registration data from onboarding slice
    yield put(resetRegistration());

    // Navigation will be handled by the component
  } catch (error: any) {
    
    let errorMessage = 'An error occurred during registration. Please try again.';
    
    if (error.response?.data) {
      const errorData = error.response.data as RegisterError;
      
      // Handle specific error cases
      const statusCode = errorData.customStatusCode || errorData.custom_status_code;
      if (statusCode === 1003) {
        errorMessage = 'This email address is already registered. Please log in or use a different email.';
      } else if (errorData.detail) {
        errorMessage = errorData.detail;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    }

    yield put(setRegistrationError(errorMessage));
    yield put(registrationFailure(errorMessage));
  } finally {
    yield put(setRegistrationLoading(false));
  }
}

export function* onboardingSaga() {
  yield takeLatest(submitRegistration.type, handleSubmitRegistration);
}